/**
 * Utility functions for handling links, including external link detection
 */

const INTERNAL_DOMAINS = [
  "triptojapan.com",
  "www.triptojapan.com",
  "admin.triptojapan.com",
  // Development domains
  "localhost",
  "127.0.0.1",
  "localhost:3000",
  "localhost:3001",
] as const;

/**
 * Determines if a URL is external to our site
 */
export function isExternalUrl(url: string): boolean {
  try {
    // Handle relative URLs - they're internal by definition
    if (url.startsWith("/") || url.startsWith("#") || url.startsWith("?")) {
      return false;
    }

    // Handle mailto:, tel:, etc. - treat as external for new tab behavior
    if (
      url.includes(":") &&
      !url.startsWith("http://") &&
      !url.startsWith("https://")
    ) {
      return true;
    }

    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // Check if hostname matches any of our internal domains
    return !INTERNAL_DOMAINS.some(
      (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
    );
  } catch {
    // Invalid URL - treat as external to be safe
    return true;
  }
}

/**
 * Returns appropriate attributes for links based on whether they're external
 */
export function getLinkAttributes(url: string): {
  target?: string;
  rel?: string;
} {
  if (isExternalUrl(url)) {
    return {
      target: "_blank",
      rel: "noopener noreferrer",
    };
  }

  return {};
}
